import {
  RECORDING_CONSENT_REQUIRED_PEER,
  RECORDING_CONSENT_REQUIRED_SELF,
} from '@shared/calls/recording-consent'
import type { UnconsentedCallPerson } from '../../../stores/recording-consent-peer-store'
import type { ChatUser } from '../../../types/chat'
import { consentFirstName, recordingConsentRejectionFromError } from './recording-consent-required'

export type GroupInviteOutcome = {
  user: Pick<ChatUser, 'userId' | 'givenName' | 'displayName'>
  /** What the INVITE threw, or null when it was sent. */
  error: unknown
}

export type GroupInviteConsentSummary = {
  /** Invitees the server did not ring because they haven't accepted call recording. */
  skipped: UnconsentedCallPerson[]
  /** The HOST hasn't accepted call recording (every INVITE was refused for it). */
  selfRequired: boolean
  /** Every invitee was refused for consent — nobody is left to call. */
  nobodyLeft: boolean
}

/**
 * Group calls send one INVITE per invitee, so the server skips an unconsented
 * invitee by refusing just that INVITE. This folds the per-invitee results
 * into what the host's client does: who was skipped, whether the host
 * themself must accept first, and whether anyone is left to call.
 */
export function summarizeGroupInviteConsent(
  outcomes: readonly GroupInviteOutcome[],
): GroupInviteConsentSummary {
  const skipped: UnconsentedCallPerson[] = []
  let selfRefusals = 0
  let consentRefusals = 0
  for (const { user, error } of outcomes) {
    const rejection = error ? recordingConsentRejectionFromError(error) : null
    if (!rejection) continue
    consentRefusals += 1
    if (rejection.code === RECORDING_CONSENT_REQUIRED_SELF) {
      selfRefusals += 1
    } else if (rejection.code === RECORDING_CONSENT_REQUIRED_PEER) {
      skipped.push({ userId: user.userId, firstName: rejection.firstName || consentFirstName(user) })
    }
  }
  const total = outcomes.length
  return {
    skipped,
    // The server checks the caller first, so one `_SELF` means the host must
    // accept; don't wait for every invitee in the batch to come back `_SELF`.
    selfRequired: selfRefusals > 0,
    nobodyLeft: total > 0 && consentRefusals === total,
  }
}
